import { CSSProperties, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IconButton, Row } from '@tempmee/react';
import { blockRecentProfessional } from '~/actions';
// NOTE(DS migration): ConfirmPopup + SmartTooltip are commonComponents with no DS equivalent
// yet — kept (separate shared-component migration category). DS IconButton has no built-in
// tooltip, so SmartTooltip preserves the "Block" hint.
import { ConfirmPopup } from '~/commonComponents';
import { SmartTooltip } from '~/commonComponents/SmartTooltip/SmartTooltip';
import blockIcon from '~/images/block-user.svg';
import BlockCrossIcon from '~/images/icons/BlockCrossIcon';
import WarningIcon from '~/images/warning-icon.svg';
import { useCheckPaymentExpiration } from '~/components/Inbox/Notification/Reminders/hooks/useCheckPaymentExpiration';
import { useUserSelector } from '~/hooks/useUserSelector';
import history from '~/browserHistory';
import { routes } from '~/routes';
import { InviteButton } from '../InviteButton';
import { IProfessionalCardUser } from '../../interfaces';

// ── inline styles (no DS prop equivalent) ──────────────────────────────────
/** marginLeft/marginRight + the bespoke 26px gap have no DS token */
const actionsRowStyle: CSSProperties = { marginLeft: 24, marginRight: 30, gap: 26 };
/** block icon sizing inside the DS IconButton circle */
const blockIconStyle: CSSProperties = { width: 24, height: 24 };
/** spacing under the warning icon inside ConfirmPopup */
const warningIconStyle: CSSProperties = { marginBottom: 21 };

/** Invite-tracking source — also determines which pro-category flag the InviteButton gets. */
type TBlockInviteSource = 'worked_with' | 'new_to_you';

interface IBlockAndInviteActionsProps {
  user: IProfessionalCardUser;
  source: TBlockInviteSource;
}

/**
 * Shared "block + invite" action cluster used by the Worked With and New to You card lists.
 * The two lists differ only by `source` (and the matching pro-category flag), so everything
 * else — the block button, the block-confirm + missing-payment popups, the payment-expiry
 * gate — lives here once.
 */
export const BlockAndInviteActions = ({ user: cardUser, source }: IBlockAndInviteActionsProps) => {
  const dispatch = useDispatch();
  const user = useUserSelector();
  const [showConfirmBlockPopup, setShowConfirmBlockPopup] = useState(false);
  const [showMissingPaymentPopup, setShowMissingPaymentPopup] = useState(false);

  const { isExpired, payments } = useCheckPaymentExpiration();

  const isUserPreApproved = user?.is_pre_approved;

  return (
    <Row justify="center" align="center" style={actionsRowStyle}>
      {/* DS IconButton 'tertiary' = bg-neutral-400 (disabled: neutral-200) at 50px — matches the
          legacy grey block circle. SmartTooltip restores the "Block" hint DS IconButton lacks. */}
      <SmartTooltip
        content="Block"
        position="bottom"
        alignment="center"
        trigger="hover"
        className="smart-tooltip"
      >
        <IconButton
          data-cy="block-button"
          variant="tertiary"
          Icon={<img src={blockIcon} alt="Block" style={blockIconStyle} />}
          onClick={() => setShowConfirmBlockPopup(true)}
          disabled={!cardUser?.isAvailable}
        />
      </SmartTooltip>
      <InviteButton
        user={{
          id: cardUser?.id,
          image: cardUser?.profileImage,
          name: cardUser?.fullname,
          profession: cardUser?.profession,
          professions: cardUser?.professions,
          ...(source === 'worked_with' ? { isRecent: true } : { isRecommendedPros: true }),
        }}
        onValidate={() => {
          if (!isUserPreApproved && (!payments?.length || isExpired)) {
            setShowMissingPaymentPopup(true);
            return false;
          }
          return true;
        }}
        disabled={!cardUser?.isAvailable}
        source={source}
      />

      {showConfirmBlockPopup && (
        // @ts-expect-error - missing type
        <ConfirmPopup
          title="Block Professional"
          titleStyle={{ marginTop: 20 }}
          description="Are you sure you want to block this professional from working with you?"
          leftButtonText="Go Back"
          leftButtonAction={() => setShowConfirmBlockPopup(false)}
          rightButtonText="Continue"
          rightButtonAction={() => {
            dispatch(blockRecentProfessional({ user: cardUser }));
            setShowConfirmBlockPopup(false);
          }}
          Icon={() => <BlockCrossIcon />}
          closePopup={() => setShowConfirmBlockPopup(false)}
        />
      )}

      {showMissingPaymentPopup && (
        // @ts-expect-error - missing type
        <ConfirmPopup
          title="Payment Method Missing or Expired"
          description="In order to invite a professional, please add or update your preferred payment method."
          leftButtonText="Go Back"
          leftButtonAction={() => setShowMissingPaymentPopup(false)}
          rightButtonText="Add Payment"
          rightButtonAction={() => {
            setShowMissingPaymentPopup(false);
            history.push(routes.paymentInformation);
          }}
          closePopup={() => setShowMissingPaymentPopup(false)}
          Icon={() => <img src={WarningIcon} alt="Warning" style={warningIconStyle} />}
        />
      )}
    </Row>
  );
};
